import { jwtDecode, STORAGE_KEY_ACCESS, isValidToken, STORAGE_KEY_REFRESH } from './utils';

import type { DecodedJWT, RoutePermissionConfig, PermissionModule, PermissionAction, FeatureType } from '../types/permissions';
import { useAuthStore } from '../store/auth.store';

function resolveModuleCandidates(module: PermissionModule): string[] {
  const candidates = [module as string];

  if (module === 'appEstablishmentCategory') {
    candidates.push('appEstablishmentCategories');
  }

  if (module === 'appEstablishmentCategories') {
    candidates.push('appEstablishmentCategory');
  }

  // Benefit categories are static and no longer used for permission control, so we can ignore them in candidate resolution
  // if (module === 'appBenefitCategory') {
  //   candidates.push('appBenefitCategories');
  // }

  // if (module === 'appBenefitCategories') {
  //   candidates.push('appBenefitCategory');
  // }

  return candidates;
}

async function getNewTokenUsingRefresh(): Promise<string | null> {
  const refreshToken = localStorage.getItem(STORAGE_KEY_REFRESH);
  if (!refreshToken || !isValidToken(refreshToken)) {
    return Promise.resolve(null);
  }

  return Promise.resolve(await useAuthStore.getState().refreshAccessToken());
}

// Função que usa o jwtDecode existente para extrair permissões
export function extractPermissionsFromToken(token: string): DecodedJWT | null {
  try {
    if (!token || typeof token !== 'string') {
      return null;
    }

    const decoded = jwtDecode(token);

    // Verifica se tem as propriedades de permissões esperadas
    if (!decoded.permissions || !decoded.features) {
      console.warn('Token does not contain permissions/features structure');
      return null;
    }

    return {
      permissions: decoded.permissions,
      features: decoded.features,
      adminId: decoded.adminId,
      undemotable: Boolean(decoded.undemotable),
      dbName: decoded.dbName,
      clientName: decoded.clientName,
      tokenType: decoded.tokenType,
      exp: decoded.exp,
      iat: decoded.iat,
      iss: decoded.iss,
      sub: decoded.sub,
      aud: decoded.aud,
    };
  } catch (error) {
    console.error('Failed to extract permissions from token:', error);
    return null;
  }
}

// Função que obtém permissões do token atual no localStorage
export async function getCurrentTokenPermissions(): Promise<DecodedJWT | null> {
  try {
    const token = localStorage.getItem(STORAGE_KEY_ACCESS);

    if (!token || !isValidToken(token)) {
      const newAccess = await getNewTokenUsingRefresh();
      if (!newAccess) {
        return null;
      }
      return extractPermissionsFromToken(newAccess ?? '');
    }
    return extractPermissionsFromToken(token);
  } catch (error) {
    console.error('Failed to get current token permissions:', error);
    return null;
  }
}

// Função para verificar se token tem feature específica
export function tokenHasFeature(token: string | null, feature: FeatureType): boolean {
  try {
    const permissions = extractPermissionsFromToken(token!);
    return Boolean(permissions?.features?.[feature]);
  } catch (error) {
    console.error(`Failed to check feature ${feature}:`, error);
    return false;
  }
}

// Função para verificar permissão de módulo
export function tokenHasModulePermission(token: string, module: PermissionModule, action?: PermissionAction): boolean {
  try {
    const permissions = extractPermissionsFromToken(token);
    if (!permissions?.permissions) {
      return false;
    }

    const modules = resolveModuleCandidates(module);

    return modules.some((moduleKey) => {
      const modulePermissions = (permissions.permissions as any)[moduleKey];

      if (!modulePermissions) {
        return false;
      }

      // Se o módulo tem valor boolean (ex: motorVariables)
      if (typeof modulePermissions === 'boolean') {
        return modulePermissions;
      }

      // Se não especificou ação, verifica se o módulo existe
      if (!action) {
        return Boolean(modulePermissions);
      }

      // Verifica ação específica
      return Boolean(modulePermissions[action]);
    });
  } catch (error) {
    console.error(`Failed to check module permission ${module}:`, error);
    return false;
  }
}

// Decodifica o JWT e retorna as informações de permissão
export function decodeJWTPermissions(token: string): DecodedJWT | null {
  try {
    return jwtDecode(token) as DecodedJWT;
  } catch (error) {
    console.error('Failed to decode JWT permissions:', error);
    return null;
  }
}

// Verifica se o usuário tem uma feature específica
export function hasFeature(jwt: DecodedJWT, feature: FeatureType): boolean {
  return Boolean(jwt?.features?.[feature]);
}

// Versão otimizada que usa suas funções para verificar feature no token atual
export function hasCurrentFeature(feature: FeatureType): boolean {
  const token = localStorage.getItem(STORAGE_KEY_ACCESS);
  if (!token) return false;

  return tokenHasFeature(token, feature);
}

// Verifica se o usuário tem permissão para um módulo e ação específicos
export function hasModulePermission(jwt: DecodedJWT, module: PermissionModule, action?: PermissionAction): boolean {
  if (!jwt?.permissions) {
    return false;
  }

  const modules = resolveModuleCandidates(module);

  return modules.some((moduleKey) => {
    const modulePermissions = (jwt.permissions as any)[moduleKey];

    if (!modulePermissions) {
      return false;
    }

    // Se o módulo tem valor boolean (ex: motorVariables)
    if (typeof modulePermissions === 'boolean') {
      return modulePermissions;
    }

    // Se não especificou ação, verifica se o módulo existe
    if (!action) {
      return Boolean(modulePermissions);
    }

    // Verifica ação específica
    return Boolean(modulePermissions[action]);
  });
}

// Versão otimizada que usa suas funções para verificar módulo no token atual
export function hasCurrentModulePermission(module: PermissionModule, action?: PermissionAction): boolean {
  const token = localStorage.getItem(STORAGE_KEY_ACCESS);
  if (!token) return false;

  return tokenHasModulePermission(token, module, action);
}

// Verifica se o usuário atende aos critérios de permissão da rota
export function checkRoutePermission(jwt: DecodedJWT | null, config: RoutePermissionConfig): boolean {
  const { feature, module, action, requireAll = false } = config;

  // Verificações individuais
  const hasFeatureAccess = feature && jwt ? hasFeature(jwt, feature) : !feature;
  const hasModuleAccess = module && jwt ? hasModulePermission(jwt, module, action) : !module;

  if (requireAll) {
    // Precisa de AMBAS as permissões
    return hasFeatureAccess && hasModuleAccess;
  }

  // Precisa de PELO MENOS UMA permissão
  if (feature && module) {
    return hasFeatureAccess || hasModuleAccess;
  }

  // Se só tem feature ou só tem module, usa a verificação respectiva
  return hasFeatureAccess && hasModuleAccess;
}

// Obtém as informações de permissão do token atual do localStorage
export async function getCurrentUserPermissions(): Promise<DecodedJWT | null> {
  try {
    return (await getCurrentTokenPermissions()) as DecodedJWT | null;
  } catch (error) {
    console.error('Failed to get current user permissions:', error);
    return null;
  }
}

// Hook personalizado para verificar permissões em componentes
export function usePermissionCheck() {
  const getCurrentPermissions = async (): Promise<DecodedJWT | null> => getCurrentUserPermissions();

  const checkPermission = async (config: RoutePermissionConfig): Promise<boolean> => {
    const jwt = await getCurrentPermissions();
    return checkRoutePermission(jwt, config);
  };

  const checkFeature = (feature: FeatureType): boolean => hasCurrentFeature(feature);

  const checkModule = (module: PermissionModule, action?: PermissionAction): boolean => hasCurrentModulePermission(module, action);

  return {
    getCurrentPermissions,
    checkPermission,
    checkFeature,
    checkModule,
  };
}
